import { api } from "@/lib/api";
import type {
  AlertCategory,
  AlertInstance,
  AlertRule,
  AlertRuleV2,
  NotificationChannel,
  RecipientRole,
  RoleDetail,
  UserSubscription,
} from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsTabVisible } from "./use-tab-visible";

export function useActiveAlerts() {
  const isVisible = useIsTabVisible();
  return useQuery({
    queryKey: ["alerts", "active"],
    queryFn: () => api<AlertInstance[]>("/api/ops/v1/alerts"),
    refetchInterval: isVisible ? 15_000 : false,
  });
}

export function useAlertHistory() {
  return useQuery({
    queryKey: ["alerts", "history"],
    queryFn: () => api<AlertInstance[]>("/api/ops/v1/alerts/history"),
  });
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) =>
      api(`/api/ops/v1/alerts/${alertId}/acknowledge`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
}

export function useSilenceAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { alertId: string; duration_minutes: number }) =>
      api(`/api/ops/v1/alerts/${params.alertId}/silence`, {
        method: "POST",
        body: JSON.stringify({ duration_minutes: params.duration_minutes }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
}

export function useAlertRules() {
  return useQuery({
    queryKey: ["alerts", "rules"],
    queryFn: () => api<AlertRule[]>("/api/ops/v1/alerts/rules"),
  });
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<AlertRule, "id" | "created_at" | "updated_at">) =>
      api<AlertRule>("/api/ops/v1/alerts/rules", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "rules"] }),
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; data: Partial<AlertRule> }) =>
      api<AlertRule>(`/api/ops/v1/alerts/rules/${params.id}`, {
        method: "PUT",
        body: JSON.stringify(params.data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "rules"] }),
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => api(`/api/ops/v1/alerts/rules/${ruleId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "rules"] }),
  });
}

// ─── V2 Hooks ───

export function useAlertCategories() {
  return useQuery({
    queryKey: ["alerts", "v2", "categories"],
    queryFn: () => api<AlertCategory[]>("/api/ops/v1/alerts/v2/categories"),
  });
}

export function useAlertRulesV2() {
  return useQuery({
    queryKey: ["alerts", "v2", "rules"],
    queryFn: () => api<AlertRuleV2[]>("/api/ops/v1/alerts/v2/rules"),
  });
}

export function useCreateRuleV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Omit<
        AlertRuleV2,
        "id" | "created_at" | "updated_at" | "category_slug" | "category_name" | "supports_platform"
      >,
    ) =>
      api<AlertRuleV2>("/api/ops/v1/alerts/v2/rules", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "rules"] }),
  });
}

export function useUpdateRuleV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: number; data: Partial<AlertRuleV2> }) =>
      api<AlertRuleV2>(`/api/ops/v1/alerts/v2/rules/${params.id}`, {
        method: "PUT",
        body: JSON.stringify(params.data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "rules"] }),
  });
}

export function useDeleteRuleV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: number) =>
      api(`/api/ops/v1/alerts/v2/rules/${ruleId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "rules"] }),
  });
}

export function useChannels() {
  return useQuery({
    queryKey: ["alerts", "v2", "channels"],
    queryFn: () => api<NotificationChannel[]>("/api/ops/v1/alerts/v2/channels"),
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<NotificationChannel, "id" | "created_at">) =>
      api<NotificationChannel>("/api/ops/v1/alerts/v2/channels", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "channels"] }),
  });
}

export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: number; data: Partial<NotificationChannel> }) =>
      api<NotificationChannel>(`/api/ops/v1/alerts/v2/channels/${params.id}`, {
        method: "PUT",
        body: JSON.stringify(params.data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "channels"] }),
  });
}

export function useDeleteChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: number) =>
      api(`/api/ops/v1/alerts/v2/channels/${channelId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "channels"] }),
  });
}

export function useTestChannel() {
  return useMutation({
    mutationFn: (channelId: number) =>
      api(`/api/ops/v1/alerts/v2/channels/${channelId}/test`, { method: "POST" }),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ["alerts", "v2", "roles"],
    queryFn: () => api<RecipientRole[]>("/api/ops/v1/alerts/v2/roles"),
  });
}

export function useRoleDetail(roleId: number) {
  return useQuery({
    queryKey: ["alerts", "v2", "roles", roleId],
    queryFn: () => api<RoleDetail>(`/api/ops/v1/alerts/v2/roles/${roleId}`),
    enabled: roleId > 0,
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description: string }) =>
      api<RecipientRole>("/api/ops/v1/alerts/v2/roles", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "roles"] }),
  });
}

export function useAddRoleMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { roleId: number; userId: number }) =>
      api(`/api/ops/v1/alerts/v2/roles/${params.roleId}/members`, {
        method: "POST",
        body: JSON.stringify({ user_id: params.userId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "roles"] }),
  });
}

export function useRemoveRoleMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { roleId: number; userId: number }) =>
      api(`/api/ops/v1/alerts/v2/roles/${params.roleId}/members`, {
        method: "DELETE",
        body: JSON.stringify({ user_id: params.userId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "roles"] }),
  });
}

export function useMySubscriptions() {
  return useQuery({
    queryKey: ["alerts", "v2", "subscriptions"],
    queryFn: () => api<UserSubscription[]>("/api/ops/v1/alerts/v2/subscriptions"),
  });
}

export function useUpdateSubscriptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<UserSubscription>[]) =>
      api<UserSubscription[]>("/api/ops/v1/alerts/v2/subscriptions", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts", "v2", "subscriptions"] }),
  });
}
